import { useEffect, useState } from "react";
import { Rss, PlayCircle, MessageCircle } from "lucide-react";
import { fetchNewsSources, MODEL_LABELS } from "../../lib/newsApi";
import { SideChip, PerspectiveChip } from "./NewsCard";

const MODEL_INFO = {
  public: "Funded mainly by government appropriation, with arm's-length editorial independence.",
  corporate: "Owned by a for-profit company or investment group; funded by ads, subscriptions or carriage fees.",
  nonprofit: "Run by a nonprofit or charity; funded by donations, foundations or grants.",
  "member-funded": "Supported mainly by recurring reader memberships.",
  independent: "Privately held, not part of a large media conglomerate.",
};
const KIND_ICON = { rss: Rss, video: PlayCircle, social: MessageCircle };

export const SourcesView = ({ onFilter }) => {
  const [sources, setSources] = useState(null);
  const [model, setModel] = useState("");
  useEffect(() => { fetchNewsSources().then(setSources).catch(() => setSources([])); }, []);
  const list = (sources || []).filter((s) => !model || s.ownership.model === model);
  return (
    <div data-testid="sources-view">
      <h2 className="font-serif text-2xl text-slate-900">Ownership transparency</h2>
      <p className="mt-1 max-w-3xl text-sm text-slate-500">Who owns each outlet, how it's funded, and the approximate editorial perspective it's commonly rated at. National, independent and community news organizations.</p>
      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {Object.entries(MODEL_INFO).map(([m, info]) => (
          <button key={m} onClick={() => setModel(model === m ? "" : m)} aria-pressed={model === m} data-testid={`ownership-model-${m}`}
            className={`rounded-2xl border p-3 text-left transition-colors ${model === m ? "border-sky-900 bg-sky-50" : "border-slate-200 bg-white hover:border-sky-300"}`}>
            <span className="font-heading text-sm font-semibold text-slate-900">{MODEL_LABELS[m]} · {(sources || []).filter((s) => s.ownership.model === m).length}</span>
            <span className="mt-1 block text-xs text-slate-500">{info}</span>
          </button>
        ))}
      </div>
      <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {list.map((s) => (
          <div key={s.id} className="flex min-w-0 flex-col break-words rounded-2xl border border-slate-200 bg-white p-5" data-testid={`source-card-${s.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div><p className="font-heading text-lg font-semibold text-slate-900">{s.name}</p><p className="text-xs text-slate-500">{s.group}{s.language === "fr" ? " · French" : ""}</p></div>
              <SideChip side={s.side} />
            </div>
            <dl className="mt-3 space-y-1.5 text-sm">
              <div><dt className="inline text-slate-500">Owner: </dt><dd className="inline text-slate-800" data-testid={`source-owner-${s.id}`}>{s.ownership.owner}</dd></div>
              <div><dt className="inline text-slate-500">Model: </dt><dd className="inline text-slate-800">{MODEL_LABELS[s.ownership.model]}</dd></div>
              <div><dt className="inline text-slate-500">Funding: </dt><dd className="inline text-slate-800">{s.ownership.funding}</dd></div>
              {s.ownership.notes && <div className="text-xs text-slate-500">{s.ownership.notes}</div>}
            </dl>
            <div className="mt-3 flex items-center gap-3 text-xs text-slate-500"><PerspectiveChip value={s.perspective} /><span>{s.items_7d} items · 7 days</span></div>
            <p className="mt-2 text-xs text-slate-500">Paired with: {s.paired_with.join(", ") || "—"}</p>
            <div className="mt-auto flex items-center justify-between gap-3 pt-4">
              <span className="flex min-w-0 flex-wrap gap-1.5">{s.feeds.map((f) => { const I = KIND_ICON[f.kind]; return <I key={f.url} size={14} className={f.ok === false ? "text-rose-400" : "text-slate-400"} title={f.outlet} />; })}</span>
              <button onClick={() => onFilter({ source: s.id })} className="shrink-0 whitespace-nowrap text-sm font-medium text-sky-800 hover:underline" data-testid={`source-view-feed-${s.id}`}>View coverage</button>
            </div>
          </div>
        ))}
      </div>
      <p className="mt-6 text-xs text-slate-500">Perspective labels are approximate, outlet-level and drawn from public media-rating services; they don't describe every article. Ownership details are from public records and may change — tell us about corrections.</p>
    </div>
  );
};
